import { getEnvVars } from '@environment';
import { GoogleSignin } from '@react-native-google-signin/google-signin';
import {
  QueryClient,
  UseMutationOptions,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { AdminActions } from '@unistocks/features/admin/store/admin.slice';
import { PortfolioActions } from '@unistocks/features/portfolios/store/portfolio.slice';
import { SavingsGoalActions } from '@unistocks/features/savings-goals/store/savings-goals.slice';
import { WatchlistActions } from '@unistocks/features/watchlists/store/watchlist.slice';
import { TENANT_HEADER } from '@unistocks/shared/consts';
import { useAppDispatch, useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { APP_CONFIG_QUERY_KEY } from '@unistocks/shared/hooks/use-application-configuration';
import { Logger } from '@unistocks/shared/services/logger.service';
import { storage } from '@unistocks/shared/storage';
import { AppActions, selectAdminToken, selectToken } from '@unistocks/shared/store/root.slice';
import { noop } from '@unistocks/shared/utils/util';
import { AuthTokenDto, ExternalAuthenticationDto, LoginInput } from '@unistocks/types/auth';
import { authClient as axios } from './axios';

const { oAuthConfig } = getEnvVars();

const invalidateAppConfig = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: APP_CONFIG_QUERY_KEY });

export const login = async ({ username, password, tenantId }: LoginInput) => {
  const { data } = await axios.post<AuthTokenDto>(
    '/connect/token',
    `grant_type=password&scope=${
      oAuthConfig.scope
    }&username=${username}&password=${encodeURIComponent(password)}&client_id=${
      oAuthConfig.clientId
    }`,
    {
      headers: {
        [TENANT_HEADER]: tenantId
      }
    }
  );

  return data;
};

export const loginExternal = async (input: ExternalAuthenticationDto) => {
  let body = `grant_type=External&scope=${oAuthConfig.scope}&client_id=${oAuthConfig.clientId}`;

  for (const [key, value] of Object.entries(input)) {
    body += `&${key}=${value}`;
  }

  const { data } = await axios.post<AuthTokenDto>('/connect/token', body);
  return data;
};

export const refresh = (refreshToken: string) =>
  axios
    .post<AuthTokenDto>(
      '/connect/token',
      `grant_type=refresh_token&scope=${oAuthConfig.scope}&refresh_token=${refreshToken}&client_id=${oAuthConfig.clientId}`
    )
    .then(({ data }) => data);

export interface ImpersonateInput {
  tenantId: string;
  accessToken: string;
}

export const impersonate = async ({ tenantId, accessToken }: ImpersonateInput) => {
  const { data } = await axios.post<AuthTokenDto>(
    '/connect/token',
    `grant_type=Impersonation&scope=${oAuthConfig.scope}&client_id=${oAuthConfig.clientId}&TenantId=${tenantId}&access_token=${encodeURIComponent(accessToken)}`
  );

  return data;
};

export const logout = ({
  token,
  tokenType
}: {
  token: string;
  tokenType: 'access_token' | 'refresh_token';
}) =>
  axios
    .post<AuthTokenDto>(
      '/connect/revocat',
      `client_id=${oAuthConfig.clientId}&token=${token}&token_type_hint=${tokenType}`,
      {
        timeout: 5_000
      }
    )
    .then(({ status }) => status === 200);

export const useLoginMutation = (
  config?: Omit<UseMutationOptions<AuthTokenDto, unknown, LoginInput>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    mutationKey: ['login', 'password'],
    mutationFn: login,
    onSuccess: (...params) => {
      invalidateAppConfig(queryClient);
      config?.onSuccess?.(...params);
    }
  });
};

export const useLoginExternalMutation = (
  config?: Omit<UseMutationOptions<AuthTokenDto, unknown, ExternalAuthenticationDto>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    mutationKey: ['login', 'external'],
    mutationFn: loginExternal,
    onSuccess: (...params) => {
      invalidateAppConfig(queryClient);
      config?.onSuccess?.(...params);
    }
  });
};

export const useRefreshTokenMutation = (
  config?: Omit<UseMutationOptions<AuthTokenDto, unknown, string>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['login', 'refresh'],
    mutationFn: refresh,
    onSuccess: (...params) => {
      invalidateAppConfig(queryClient);
      config?.onSuccess?.(...params);
    }
  });
};

export const useImpersonateMutation = (
  config?: Omit<UseMutationOptions<AuthTokenDto, unknown, ImpersonateInput>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    mutationKey: ['login', 'impersonate'],
    mutationFn: impersonate,
    onSuccess: (...params) => {
      queryClient.clear();
      config?.onSuccess?.(...params);
    }
  });
};

export const useLogoutMutation = () => {
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectToken);
  const adminToken = useAppSelector(selectAdminToken);
  const queryClient = useQueryClient();
  const isImpersonating = !!adminToken;

  const mutationFn = async () => {
    const logoutPromises: Promise<any>[] = [];

    if (token?.access_token)
      logoutPromises.push(
        logout({
          token: token.access_token,
          tokenType: 'access_token'
        })
      );

    if (token?.refresh_token)
      logoutPromises.push(
        logout({
          token: token.refresh_token,
          tokenType: 'refresh_token'
        })
      );

    if (!isImpersonating) {
      logoutPromises.push(GoogleSignin.signOut().catch(noop)); // Ignore errors from Google Sign-In
    }

    try {
      await Promise.all(logoutPromises);
    } catch {}
  };

  return useMutation({
    mutationFn,
    mutationKey: ['logout'],
    retry: false,
    onMutate: () => Logger.debug('Logging out'),
    onSettled: () => {
      if (isImpersonating) {
        dispatch(AppActions.endImpersonation());
      } else {
        dispatch(AppActions.clear());
        storage.clearAll();
      }
      dispatch(PortfolioActions.clear());
      dispatch(WatchlistActions.clear());
      dispatch(SavingsGoalActions.clear());
      dispatch(AdminActions.clear());
      queryClient.invalidateQueries();
      queryClient.clear();
    },
    onSuccess: () => Logger.debug('Successfully logged out'),
    onError: error => Logger.error(error, 'Failed to logout')
  });
};
